import React, { useState, useEffect } from 'react';
import { Users, Download, Save, Edit, Check, X, RefreshCw, Building, UserCheck, Trash2, Send, Copy, CheckCircle, Clock, ExternalLink, Eye } from 'lucide-react';
import ApiService from '../services/ApiService';

const ContactsScreen = () => {
  const [contacts, setContacts] = useState([]);
  const [contractorInvites, setContractorInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSyncedMode, setIsSyncedMode] = useState(false); // Track if we're showing synced data

  useEffect(() => {
    loadEnhancedContacts();
    loadContractorInvites();
  }, []);

  const loadContractorInvites = async () => {
    try {
      const response = await ApiService.get('/contractor/invites');
      setContractorInvites(response || []);
    } catch (error) {
      console.error('Error loading contractor invites:', error);
      setContractorInvites([]);
    }
  };

  const getContractorInviteStatus = (contactId) => {
    const invite = contractorInvites.find(inv => 
      (inv.xeroContactId === contactId) || (inv.XeroContactId === contactId)
    );
    if (!invite) return null;
    
    // Check if expired
    const expiresAt = invite.expiresAt || invite.ExpiresAt;
    if (expiresAt && new Date(expiresAt) < new Date()) {
      return { ...invite, status: 'Expired' };
    }
    
    return invite;
  };

  const handleViewPortal = async (contact) => {
    try {
      const xeroContactId = contact.contactID || contact.ContactID || contact.xeroContactId || contact.XeroContactId;
      const name = contact.name || contact.Name || contact.contractorName || contact.ContractorName || '';
      const result = await ApiService.apiCall(
        `/contractor/admin-preview-token?xeroContactId=${encodeURIComponent(xeroContactId)}&name=${encodeURIComponent(name)}`
      );
      const previewUrl = `${window.location.origin}/contractor/preview?token=${encodeURIComponent(result.token)}`;
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      alert(`Cannot open portal preview: ${error.message}`);
    }
  };

  const createContractorInvite = async (contact) => {
    try {
      // Check if contact has email address
      let contractorEmail = contact.emailAddress || contact.EmailAddress;
      if (!contractorEmail) {
        const email = prompt(`${contact.name} doesn't have an email address.\n\nPlease enter their email address to send the invite:`);
        if (!email) {
          alert('Email address is required to send contractor invite.');
          return;
        }
        if (!isValidEmail(email)) {
          alert('Please enter a valid email address.');
          return;
        }
        contractorEmail = email;
      }

      const inviteRequest = {
        xeroContactId: contact.contactID || contact.ContactID,
        contractorName: contact.name || contact.Name,
        email: contractorEmail,
        notes: `Contractor invite for ${contact.name || contact.Name}`
      };

      const response = await ApiService.post('/contractor/create-invite', inviteRequest);
      
      // Reload invites to get the updated list
      await loadContractorInvites();

      const inviteUrl = response.inviteUrl || response.InviteUrl;
      alert(`Invite created for ${contact.name}!\n\nEmail: ${contractorEmail}\nInvite URL: ${inviteUrl}\n\n(Copy this link and send to the contractor)`);
      
      return response;
    } catch (error) {
      console.error('Error creating invite:', error);
      alert('Failed to create invite: ' + error.message);
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };


  const copyInviteUrl = (inviteUrl) => {
    navigator.clipboard.writeText(inviteUrl);
    alert('Invite URL copied to clipboard!');
  };

  const loadEnhancedContacts = async (syncFromXero = false) => {
    try {
      setLoading(true);
      const endpoint = syncFromXero ? '/contactenhancement/enhanced-contacts?syncFromXero=true' : '/contactenhancement/enhanced-contacts';
      const response = await ApiService.apiCall(endpoint);
      
      // Track sync mode
      if (syncFromXero) {
        setIsSyncedMode(true);
      }
      
      // Debug: Log Jason Bowman's data
      const jasonContact = response.find(c => (c.Name || c.name || '').includes('Jason Bowman'));
      if (jasonContact) {
        console.log('🔍 Jason Bowman data:', jasonContact);
        console.log('ContactID:', jasonContact.ContactID || jasonContact.contactID);
        console.log('IsCustomer:', jasonContact.IsCustomer);
        console.log('IsSupplier:', jasonContact.IsSupplier);
      }
      
      setContacts(response);
    } catch (error) {
      console.error('Error loading enhanced contacts:', error);
      alert('Error loading contacts: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const syncFromXero = async () => {
    try {
      setSyncing(true);
      // Load contacts with Xero sync enabled
      await loadEnhancedContacts(true);
      alert(`✅ Synced contacts from Xero!`);
    } catch (error) {
      console.error('Error syncing from Xero:', error);
      alert('Error syncing from Xero: ' + error.message);
    } finally {
      setSyncing(false);
    }
  };

  const deleteAllContacts = async () => {
    if (!window.confirm('🗑️ Delete ALL contacts from local database?\n\nThis will remove all contact enhancements and local contact data.\nXero contacts will remain unchanged.\n\nAre you sure?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await ApiService.apiCall('/contactenhancement/delete-all', {
        method: 'DELETE'
      });
      
      await loadEnhancedContacts();
      alert(`✅ ${response.message}`);
    } catch (error) {
      console.error('Error deleting all contacts:', error);
      alert('Error deleting contacts: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const saveContactEnhancement = async (contactId, contactName, enhancements) => {
    try {
      const payload = {
        XeroContactId: contactId,  // Capital X to match API
        ContactName: contactName,  // Capital C to match API
        DefaultTeam: enhancements.defaultTeam || null,
        DefaultDepartment: enhancements.defaultDepartment || null,
        IsPreferredClient: enhancements.isPreferredClient || false,
        IsPreferredSupplier: enhancements.isPreferredSupplier || false,
        DefaultDiscountPercentage: enhancements.defaultDiscountPercentage || 0,
        Priority: enhancements.priority || 'Normal',
        Notes: enhancements.notes || null
      };

      console.log('💾 Saving contact enhancement:', payload);

      // Always try to update first, then create if it doesn't exist
      try {
        // Try update first
        await ApiService.apiCall(`/contactenhancement/${contactId}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        console.log('✅ Updated existing enhancement');
      } catch (updateError) {
        console.log('🔄 Update failed, trying create...', updateError);
        // If update fails, try create
        await ApiService.apiCall('/contactenhancement', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        console.log('✅ Created new enhancement');
      }

      // Reload with the same mode (synced or database-only)
      await loadEnhancedContacts(isSyncedMode);
      setEditingContact(null);
      alert('✅ Contact enhancement saved!');
    } catch (error) {
      console.error('Error saving contact enhancement:', error);
      alert('Error saving enhancement: ' + error.message);
    }
  };

  // Separate customers and suppliers based on Xero roles (contacts can be in both sections if they're both)
  const customers = contacts.filter(c => c.IsCustomer || c.isCustomer);
  const suppliers = contacts.filter(c => c.IsSupplier || c.isSupplier);

  // Filter by search term
  const filteredCustomers = customers.filter(contact => {
    const contactName = contact.Name || contact.name || '';
    return !searchTerm || contactName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const filteredSuppliers = suppliers.filter(contact => {
    const contactName = contact.Name || contact.name || '';
    return !searchTerm || contactName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // No need for predefined options - using text fields

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading contacts...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
            <Users className="mr-2" />
            Contact Management
          </h2>
          <p className="text-gray-600">Set Team, Department, and Preferred status for Xero customers and suppliers</p>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={deleteAllContacts}
            disabled={loading || syncing}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete All
          </button>
          
          <button
            onClick={syncFromXero}
            disabled={syncing}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center disabled:opacity-50"
          >
            {syncing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {syncing ? 'Syncing...' : 'Sync from Xero'}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <input
          type="text"
          placeholder="Search contacts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border rounded px-3 py-2 w-64"
        />
        <span className="ml-4 text-sm text-gray-500">
          {filteredCustomers.length} customers, {filteredSuppliers.length} suppliers
          {isSyncedMode && <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">📡 Synced with Xero</span>}
        </span>
      </div>

      {/* CUSTOMERS SECTION */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b bg-blue-50">
          <h3 className="font-semibold text-blue-800 flex items-center">
            <Building className="w-5 h-5 mr-2" />
            Customers ({filteredCustomers.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 font-medium text-gray-700">Name</th>
                <th className="text-left p-3 font-medium text-gray-700">Team</th>
                <th className="text-left p-3 font-medium text-gray-700">Department</th>
                <th className="text-center p-3 font-medium text-gray-700">Preferred Customer</th>
                <th className="text-center p-3 font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((contact) => {
                const contactId = contact.ContactID || contact.contactID;
                return (
                  <ContactRow
                    key={`customer-${contactId}`}
                    contact={contact}
                    contactType="customer"
                    isEditing={editingContact === `customer-${contactId}`}
                    onEdit={() => setEditingContact(`customer-${contactId}`)}
                    onCancel={() => setEditingContact(null)}
                    onSave={(enhancements) => saveContactEnhancement(contactId, contact.Name || contact.name, enhancements)}
                  />
                );
              })}
            </tbody>
          </table>
          {filteredCustomers.length === 0 && (
            <div className="p-6 text-center text-gray-500">No customers found</div>
          )}
        </div>
      </div>

      {/* SUPPLIERS SECTION */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b bg-green-50">
          <h3 className="font-semibold text-green-800 flex items-center">
            <UserCheck className="w-5 h-5 mr-2" />
            Suppliers ({filteredSuppliers.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 font-medium text-gray-700">Name</th>
                <th className="text-left p-3 font-medium text-gray-700">Team</th>
                <th className="text-left p-3 font-medium text-gray-700">Department</th>
                <th className="text-center p-3 font-medium text-gray-700">Preferred Supplier</th>
                <th className="text-center p-3 font-medium text-gray-700">Portal Access</th>
                <th className="text-center p-3 font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSuppliers.map((contact) => {
                const contactId = contact.ContactID || contact.contactID;
                return (
                  <ContactRow
                    key={`supplier-${contactId}`}
                    contact={contact}
                    contactType="supplier"
                    isEditing={editingContact === `supplier-${contactId}`}
                    onEdit={() => setEditingContact(`supplier-${contactId}`)}
                    onCancel={() => setEditingContact(null)}
                    onSave={(enhancements) => saveContactEnhancement(contactId, contact.Name || contact.name, enhancements)}
                    inviteStatus={getContractorInviteStatus(contactId)}
                    onCreateInvite={() => createContractorInvite(contact)}
                    onCopyInviteUrl={(url) => copyInviteUrl(url)}
                    onViewPortal={() => handleViewPortal(contact)}
                  />
                );
              })}
            </tbody>
          </table>
          {filteredSuppliers.length === 0 && (
            <div className="p-6 text-center text-gray-500">No suppliers found</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Individual contact row component
const ContactRow = ({ contact, contactType, isEditing, onEdit, onCancel, onSave, inviteStatus, onCreateInvite, onCopyInviteUrl, onViewPortal }) => {
  const [formData, setFormData] = useState({
    defaultTeam: contact.Enhancement?.DefaultTeam || contact.enhancement?.defaultTeam || '',
    defaultDepartment: contact.Enhancement?.DefaultDepartment || contact.enhancement?.defaultDepartment || '',
    isPreferredClient: contact.Enhancement?.IsPreferredClient || contact.enhancement?.isPreferredClient || false,
    isPreferredSupplier: contact.Enhancement?.IsPreferredSupplier || contact.enhancement?.isPreferredSupplier || false,
    priority: contact.Enhancement?.Priority || contact.enhancement?.priority || 'Normal',
    notes: contact.Enhancement?.Notes || contact.enhancement?.notes || '',
    id: contact.Enhancement?.Id || contact.enhancement?.id || null
  });

  const contactName = contact.Name || contact.name || 'Unknown';
  const isActiveCustomer = formData.isPreferredClient;
  const isActiveSupplier = formData.isPreferredSupplier;
  const currentActiveStatus = contactType === 'customer' ? isActiveCustomer : isActiveSupplier;

  const handleSave = () => {
    onSave(formData);
  };

  if (isEditing) {
    return (
      <tr className="border-b bg-yellow-50">
        <td className="p-3 font-medium">{contactName}</td>
        <td className="p-3">
          <input
            type="text"
            value={formData.defaultTeam}
            onChange={(e) => setFormData({...formData, defaultTeam: e.target.value})}
            placeholder="Enter team (e.g., jason, rich)"
            className="border rounded px-2 py-1 text-sm w-full"
          />
        </td>
        <td className="p-3">
          <input
            type="text"
            value={formData.defaultDepartment}
            onChange={(e) => setFormData({...formData, defaultDepartment: e.target.value})}
            placeholder="Enter department (e.g., GAM, MCS)"
            className="border rounded px-2 py-1 text-sm w-full"
          />
        </td>
        <td className="p-3 text-center">
          <label className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={currentActiveStatus}
              onChange={(e) => setFormData({
                ...formData,
                [contactType === 'customer' ? 'isPreferredClient' : 'isPreferredSupplier']: e.target.checked
              })}
              className="mr-1"
            />
Preferred {contactType === 'customer' ? 'Customer' : 'Supplier'}
          </label>
        </td>
        <td className="p-3 text-center">
          <div className="flex justify-center space-x-2">
            <button
              onClick={handleSave}
              className="text-green-600 hover:text-green-800"
              title="Save"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={onCancel}
              className="text-red-600 hover:text-red-800"
              title="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  // Get enhancement values with fallback for casing
  const displayTeam = contact.Enhancement?.DefaultTeam || contact.enhancement?.defaultTeam;
  const displayDepartment = contact.Enhancement?.DefaultDepartment || contact.enhancement?.defaultDepartment;
  const hasEnhancement = contact.Enhancement || contact.enhancement;

  return (
    <tr className="border-b hover:bg-gray-50">
      <td className="p-3 font-medium">
        <div className="flex items-center gap-2">
          {contactName}
          {contactType === 'supplier' && !(contact.emailAddress || contact.EmailAddress) && (
            <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded" title="No email address">
              📧 No Email
            </span>
          )}
        </div>
      </td>
      <td className="p-3 text-gray-600">
        {displayTeam || <span className="text-gray-400">Not set</span>}
      </td>
      <td className="p-3 text-gray-600">
        {displayDepartment || <span className="text-gray-400">Not set</span>}
      </td>
      <td className="p-3 text-center">
        {hasEnhancement ? (
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            currentActiveStatus ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
          }`}>
            {currentActiveStatus ? 'Preferred' : 'Standard'}
          </span>
        ) : (
          <span className="text-gray-400">Not set</span>
        )}
      </td>
      <td className="p-3 text-center">
        {contactType === 'supplier' && (
          <div className="flex flex-col items-center gap-1">
            {!inviteStatus ? (
              <div className="flex gap-1 items-center">
                <button
                  onClick={onCreateInvite}
                  className="text-green-600 hover:text-green-800 p-1 flex items-center gap-1"
                  title="Invite to Portal"
                >
                  <Send className="w-3 h-3" />
                  <span className="text-xs">Invite</span>
                </button>
                <button
                  onClick={onViewPortal}
                  className="text-indigo-600 hover:text-indigo-800 p-1"
                  title="Preview contractor portal"
                >
                  <Eye className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  inviteStatus.status === 'Accepted' ? 'bg-green-100 text-green-800' :
                  inviteStatus.status === 'Expired' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {inviteStatus.status === 'Accepted' && <CheckCircle className="w-3 h-3 mr-1" />}
                  {inviteStatus.status === 'Pending' && <Clock className="w-3 h-3 mr-1" />}
                  {inviteStatus.status}
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={onViewPortal}
                    className="text-indigo-600 hover:text-indigo-800 p-1"
                    title="Preview contractor portal"
                  >
                    <Eye className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => onCopyInviteUrl(inviteStatus.inviteUrl)}
                    className="text-blue-600 hover:text-blue-800 p-1"
                    title="Copy Invite URL"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                  <a
                    href={inviteStatus.inviteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-800 p-1"
                    title="Open Portal"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </>
            )}
          </div>
        )}
      </td>
      <td className="p-3 text-center">
        <button
          onClick={onEdit}
          className="text-blue-600 hover:text-blue-800"
          title="Edit"
        >
          <Edit className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
};

export default ContactsScreen;